import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { useTimer } from '@components-shared/src/countdown/hooks/useTimer';
import { shouldShowEndsInCountdown } from '@components/SongCards/ReleaseInfoCard';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { ModalType, useModal } from '@context/modal';
import { useSubscription } from '@graphql/graphql-ws';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, MinimalReleaseUpdatesDocument, StatsFragmentDoc } from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment Stats on Release {
    id
    finalQuantity
    supplyCutoffTimestamp
    isFirstPhaseCompleted
    mintStartTimestamp
    quantityLowerBound
    quantityUpperBound
    totalMinted

    listing {
      id
      ... on TieredEditionListing {
        tiers {
          id
          tierNumber
          totalMinted
        }
      }
    }
  }
`);

const formatCountdown = ({
  days,
  hours,
  minutes,
  seconds,
}: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}) => {
  if (days > 0) return `Ends in ${days}d`;
  if (hours > 0) return `Ends in ${hours}h`;
  if (minutes > 0) return `Ends in ${minutes}m`;
  return `Ends in ${seconds}s`;
};

const getPercentageMinted = ({
  limitedMinted,
  quantityUpperBound,
}: {
  limitedMinted: number;
  quantityUpperBound: number;
}) => `${Math.round(Math.min(limitedMinted / quantityUpperBound, 1) * 100)}% minted`;

const formatMintCount = (totalMinted: number) =>
  `${totalMinted.toLocaleString()} mint${totalMinted > 1 ? 's' : ''}`;

export function Stats({
  statsInfo,
  releaseId,
}: {
  statsInfo: FragmentType<StatsFragmentDoc>;
  releaseId: string;
}) {
  const { openModal } = useModal();
  const stats = getFragment(StatsFragmentDoc, statsInfo);
  const { mintStartTimestamp, quantityLowerBound, quantityUpperBound, listing } = stats;

  const { data: subscriptionData } = useSubscription({
    query: MinimalReleaseUpdatesDocument,
    variables: {
      id: releaseId,
    },
  });

  const { totalMinted, finalQuantity, isFirstPhaseCompleted } =
    subscriptionData?.data.releaseUpdates || stats;

  // limitedMinted is used for percent remaining
  const vipMinted = listing?.tiers.find(t => t.tierNumber === 1)?.totalMinted;
  const limitedMinted = vipMinted != null ? vipMinted : totalMinted;

  const shouldShowCountdown = shouldShowEndsInCountdown({
    finalQuantity,
    quantityUpperBound,
    quantityLowerBound,
    mintStartTimestamp,
  });

  const { days, hours, minutes, seconds } = useTimer({
    expiryTimestamp: stats.supplyCutoffTimestamp ?? Date.now(),
  });

  const countdownString = formatCountdown({ days, hours, minutes, seconds });
  const percentageMinted = getPercentageMinted({ limitedMinted, quantityUpperBound });
  const formattedMintCount = formatMintCount(stats.totalMinted);

  const fomoText = shouldShowCountdown ? countdownString : percentageMinted;
  const isPrimarySaleEndingSoon = shouldShowCountdown && days < 1 && hours < 1 && minutes < 30;

  // note: for open editions, we will always display mint count over percentage since quantityUpperBound is uint32.max
  const isLowMintPercentage = Math.round((totalMinted / quantityUpperBound) * 100) < 1;
  const shouldDisplayMintCount =
    isFirstPhaseCompleted || (isLowMintPercentage && !shouldShowCountdown);

  const onStatsClick = () => {
    openModal(ModalType.RELEASE_MINTS, { body: <ReleaseMintsModal releaseId={releaseId} /> });
  };

  const statsText = () => {
    if (totalMinted === 0) return 'Be the first to collect';

    if (shouldDisplayMintCount)
      return (
        <button
          className="transition-[filter] duration-300 hover:brightness-[70%]"
          onClick={onStatsClick}
        >
          {formattedMintCount}
        </button>
      );

    return (
      <button
        className="transition-[filter] duration-300 hover:brightness-[70%]"
        onClick={onStatsClick}
      >
        {formattedMintCount} •{' '}
        <span className={isPrimarySaleEndingSoon ? 'text-fuchsia500' : undefined}>{fomoText}</span>
      </button>
    );
  };

  return (
    <div className="mt-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-base-xs text-base500 md:text-base-s">
      {statsText()}
    </div>
  );
}
